import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  TextField,
  Grid,
  Rating,
  Divider,
  Alert,
  Card,
  CardContent,
  Avatar,
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  CircularProgress,
  IconButton,
  TablePagination,
  InputAdornment
} from '@mui/material';
import { 
  Search as SearchIcon, 
  School as SchoolIcon, 
  RateReview as FeedbackIcon, 
  Close as CloseIcon,
  Star as StarIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import api from '../services/api';

const SchoolEvaluationReport = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdminOrStaff = user.role === 'ADMIN' || user.role === 'STAFF';

  const [stats, setStats] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Detail Dialog State
  const [openDetail, setOpenDetail] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState<any>(null);
  const [schoolReviews, setSchoolReviews] = useState<any[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Edit Review Dialog State
  const [openEditReview, setOpenEditReview] = useState(false);
  const [editingReview, setEditingReview] = useState<any>(null);
  const [editRating, setEditRating] = useState<number | null>(5);
  const [editFeedback, setEditFeedback] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await api.get('/school-evaluations/stats');
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching school evaluation stats:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleOpenDetail = async (school: any) => {
    setSelectedSchool(school);
    setLoadingDetail(true);
    setOpenDetail(true);
    try {
      const res = await api.get(`/school-evaluations/school/${school.id}`);
      setSchoolReviews(res.data);
    } catch (err) {
      console.error('Error fetching school reviews:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleCloseDetail = () => {
    setOpenDetail(false);
    setSelectedSchool(null);
    setSchoolReviews([]);
  };

  const handleEditClick = (review: any) => {
    setEditingReview(review);
    setEditRating(review.rating);
    setEditFeedback(review.feedback || '');
    setOpenEditReview(true);
  };

  const handleSaveEdit = async () => {
    if (!editingReview) return;
    try {
      setSavingEdit(true);
      await api.put(`/school-evaluations/${editingReview.id}`, {
        rating: editRating,
        feedback: editFeedback
      });
      // Refresh current school reviews and overall stats
      const res = await api.get(`/school-evaluations/school/${selectedSchool.id}`);
      setSchoolReviews(res.data);
      fetchStats();
      setOpenEditReview(false);
      setEditingReview(null);
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteReview = async (reviewId: string) => {
    if (!window.confirm('คุณแน่ใจหรือไม่ที่จะลบผลการประเมินนี้?')) return;
    try {
      await api.delete(`/school-evaluations/${reviewId}`);
      // Refresh
      const res = await api.get(`/school-evaluations/school/${selectedSchool.id}`);
      setSchoolReviews(res.data);
      fetchStats();
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการลบข้อมูล');
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredStats = stats.filter((s: any) => 
    (s.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Metrics calculation
  const totalSchools = stats.length;
  const totalReviews = stats.reduce((sum, s) => sum + parseInt(s.review_count || 0), 0);
  const overallAvgRating = stats.length > 0 
    ? stats.reduce((sum, s) => sum + parseFloat(s.avg_rating || 0), 0) / stats.length 
    : 0;

  return (
    <Box sx={{ py: 1 }}>
      {/* Page Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4 
      }}>
        <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', fontSize: { xs: '1.25rem', sm: '1.75rem' }, fontWeight: 700, color: 'text.primary' }}>
          <SchoolIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 32 }} /> รายงานประเมินสถานศึกษา (School Evaluation Report)
        </Typography>
      </Box>

      {/* Metrics Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={{ 
            borderRadius: 3, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
            borderLeft: '6px solid #1976d2',
            background: 'linear-gradient(to right, #ffffff, #fcfdfe)'
          }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: '20px !important' }}>
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 600 }}>จำนวนโรงเรียนที่ถูกประเมิน</Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, color: '#1976d2' }}>{totalSchools}</Typography>
                <Typography variant="caption" color="textSecondary">โรงเรียนที่มีการประเมินจากนักศึกษา</Typography>
              </Box>
              <Avatar sx={{ bgcolor: 'rgba(25, 118, 210, 0.1)', color: '#1976d2', width: 56, height: 56 }}>
                <SchoolIcon fontSize="large" />
              </Avatar>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={{ 
            borderRadius: 3, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
            borderLeft: '6px solid #eab308',
            background: 'linear-gradient(to right, #ffffff, #fefdfc)'
          }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: '20px !important' }}>
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 600 }}>คะแนนเฉลี่ยรวมทุกโรงเรียน</Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, color: '#eab308', display: 'flex', alignItems: 'center' }}>
                  {overallAvgRating.toFixed(2)}
                  <StarIcon sx={{ ml: 0.5, color: '#eab308' }} />
                </Typography>
                <Typography variant="caption" color="textSecondary">คะแนนเต็ม 5.00 ดาว</Typography>
              </Box>
              <Avatar sx={{ bgcolor: 'rgba(234, 179, 8, 0.1)', color: '#eab308', width: 56, height: 56 }}>
                <StarIcon fontSize="large" />
              </Avatar>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={{ 
            borderRadius: 3, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
            borderLeft: '6px solid #2e7d32',
            background: 'linear-gradient(to right, #ffffff, #fcfefc)'
          }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: '20px !important' }}>
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 600 }}>จำนวนแบบประเมินที่ส่งมา</Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, color: '#2e7d32' }}>{totalReviews}</Typography>
                <Typography variant="caption" color="textSecondary">จำนวนครั้งที่นักศึกษาประเมินโรงเรียน</Typography>
              </Box>
              <Avatar sx={{ bgcolor: 'rgba(46, 125, 50, 0.1)', color: '#2e7d32', width: 56, height: 56 }}>
                <FeedbackIcon fontSize="large" />
              </Avatar>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Area */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        {/* Table Search & Toolbar */}
        <Box sx={{ p: 3, bgcolor: '#fafafa', borderBottom: '1px solid #efefef', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'center' }}>
          <TextField
            fullWidth
            placeholder="ค้นหาชื่อสถานศึกษา..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }
            }}
            size="small"
            sx={{ maxWidth: { md: 400 }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </Box>

        <TableContainer>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Table sx={{ minWidth: 650 }}>
              <TableHead sx={{ bgcolor: '#f4f6f8' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', pl: 3 }}>ชื่อสถานศึกษา</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>คะแนนเฉลี่ย</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>จำนวนผู้ประเมิน</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', pr: 3 }}>การจัดการ</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredStats
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row: any) => (
                    <TableRow key={row.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell component="th" scope="row" sx={{ pl: 3, py: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ bgcolor: 'primary.50', color: 'primary.main', mr: 2, width: 40, height: 40 }}>
                            <SchoolIcon fontSize="small" />
                          </Avatar>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>{row.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', flexDirection: 'column' }}>
                          <Rating value={parseFloat(row.avg_rating)} precision={0.1} readOnly size="small" />
                          <Typography variant="caption" sx={{ fontWeight: 'bold', mt: 0.5, color: 'text.secondary' }}>
                            {parseFloat(row.avg_rating).toFixed(2)} / 5.00
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Typography sx={{ fontWeight: 700 }}>{row.review_count}</Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ pr: 3 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<ViewIcon />}
                          onClick={() => handleOpenDetail(row)}
                          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
                        >
                          ดูรายละเอียด
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                {filteredStats.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                      <Typography color="textSecondary">ไม่พบข้อมูลผลประเมินสถานศึกษา</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredStats.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="รายการต่อหน้า:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} จาก ${count}`}
        />
      </Paper>

      {/* Detail Dialog */}
      <Dialog 
        open={openDetail} 
        onClose={handleCloseDetail} 
        fullWidth 
        maxWidth="md"
        slotProps={{
          paper: {
            sx: { borderRadius: 3, boxShadow: '0 12px 40px rgba(0,0,0,0.12)' }
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.main', color: 'white', py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SchoolIcon sx={{ mr: 1 }} />
            <Typography variant="h6" component="span" sx={{ fontWeight: 'bold' }}>
              ผลประเมินเชิงลึก - {selectedSchool?.name}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseDetail} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3, bgcolor: '#f8f9fa' }}>
          {loadingDetail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {/* School Overall Summary */}
              <Card sx={{ mb: 3, borderRadius: 2, border: '1px solid #efefef', boxShadow: 'none' }}>
                <CardContent sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', py: '16px !important' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 600 }}>คะแนนเฉลี่ย</Typography>
                    <Rating value={parseFloat(selectedSchool?.avg_rating || 0)} precision={0.1} readOnly size="medium" sx={{ mt: 0.5 }} />
                    <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main', mt: 0.5 }}>
                      {parseFloat(selectedSchool?.avg_rating || 0).toFixed(2)} / 5.00
                    </Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem />
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 600 }}>จำนวนนักศึกษาที่ให้ข้อมูล</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5 }}>
                      {selectedSchool?.review_count}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">คน</Typography>
                  </Box>
                </CardContent>
              </Card>

              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
                <FeedbackIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} /> ความคิดเห็นและข้อเสนอแนะจากนักศึกษา ({schoolReviews.length})
              </Typography>

              {schoolReviews.length === 0 && (
                <Alert severity="info">ยังไม่มีนักศึกษาส่งความเห็นเกี่ยวกับโรงเรียนนี้</Alert>
              )}

              <Grid container spacing={2}>
                {schoolReviews.map((review: any) => (
                  <Grid size={{ xs: 12 }} key={review.id}>
                    <Card sx={{ borderRadius: 2, border: '1px solid #eef0f2', boxShadow: 'none', transition: 'all 0.2s', '&:hover': { borderColor: 'primary.light', boxShadow: '0 4px 12px rgba(25, 118, 210, 0.05)' } }}>
                      <CardContent sx={{ p: 2.5 }}>
                        {/* Review Header */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                              {review.prefix || ''}{review.first_name} {review.last_name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              สาขาวิชา: {review.major || '-'} | ปีการศึกษา: {review.semester}/{review.academic_year}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Rating value={review.rating} readOnly size="small" />
                              {isAdminOrStaff && (
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <IconButton size="small" color="primary" onClick={() => handleEditClick(review)} title="แก้ไขผลประเมิน">
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton size="small" color="error" onClick={() => handleDeleteReview(review.id)} title="ลบผลประเมิน">
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              )}
                            </Box>
                            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                              {new Date(review.created_at).toLocaleDateString('th-TH')}
                            </Typography>
                          </Box>
                        </Box>
                        <Divider sx={{ my: 1, borderColor: '#f8f9fa' }} />
                        {/* Review Body */}
                        <Box sx={{ mt: 1, p: 2, bgcolor: '#fcfcfc', borderRadius: 1.5, borderLeft: '4px solid', borderColor: 'primary.light' }}>
                          <Typography variant="body2" sx={{ fontStyle: review.feedback ? 'normal' : 'italic', color: review.feedback ? 'text.primary' : 'text.secondary', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                            {review.feedback ? `"${review.feedback}"` : '"ไม่มีความเห็นและข้อเสนอแนะเพิ่มเติม"'}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDetail} variant="contained" color="primary" sx={{ borderRadius: 2, px: 3 }}>
            ปิดหน้าต่าง
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Review Dialog (Admin & Staff) */}
      <Dialog open={openEditReview} onClose={() => setOpenEditReview(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          แก้ไขผลการประเมินสถานศึกษา
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ my: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              นักศึกษา: {editingReview?.prefix || ''}{editingReview?.first_name} {editingReview?.last_name} ({editingReview?.major})
            </Typography>
            <Box sx={{ my: 2 }}>
              <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>คะแนนประเมิน (ดาว):</Typography>
              <Rating 
                value={editRating} 
                onChange={(event, newValue) => setEditRating(newValue)} 
                size="large" 
              />
            </Box>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="ความคิดเห็นและข้อเสนอแนะ"
              value={editFeedback}
              onChange={(e) => setEditFeedback(e.target.value)}
              placeholder="กรอกความคิดเห็นและข้อเสนอแนะ..."
              sx={{ mt: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpenEditReview(false)} disabled={savingEdit}>
            ยกเลิก
          </Button>
          <Button 
            onClick={handleSaveEdit} 
            variant="contained" 
            color="primary" 
            disabled={savingEdit}
            startIcon={savingEdit ? <CircularProgress size={18} /> : undefined}
          >
            บันทึกการแก้ไข
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SchoolEvaluationReport;
